import { Button, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import SearchIcon from "../../assets/icons/SearchIcon";
import SelectDownIcon from "../../assets/icons/SelectDownIcon";
import { useCreateBulkUsersMutation } from "../../features/device/deviceApi";
import { useGetEmployeeManagementDataQuery } from "../../features/employee/employeeApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { useQueryParams } from "../../hooks/useQueryParams";
import useTable from "../../hooks/useTable";
import { toast } from "../../lib/toast";
import {
  GetEmployeeManagementDataParamsType,
  GetEmployeeManagementDataResponseType,
  GetEmployeeParamsType,
  ModalComponentProps,
} from "../../types";
import { countParPageSerialNumber } from "../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  selectStringOption,
  underscoreToCapitalizeString,
} from "../../utils/utils";
import ConfirmModal from "../ui/ConfirmModal";
import CustomModal from "../ui/CustomModal";
import CustomTable from "../ui/CustomTable";

enum Status {
  active = "status-success",
  inactive = "status-danger",
  invited = "status-info",
}

type ModalType =
  | "INVITE"
  | "GROSS_SALARY"
  | "BKASH_NUMBER"
  | "BULK_ACTION"
  | "SEND_INVITATION_AGAIN"
  | "START_ENROLLMENT"
  | "CREATE_DEVICE_USER";

const searchTypeSelect = ["name", "email", "employee_id"]?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

type GetEmployeeTypeParams = {
  attendanceType?: "remote" | "location" | "wifi";
} & GetEmployeeParamsType;

export default function CreateDeviceUserModal({
  handleClose,
  // data,
  ...rest
}: ModalComponentProps<string>) {
  const [selectedData, setSelectedData] = useState<
    GetEmployeeManagementDataResponseType["data"]["employees"]
  >([]);

  const { query } = useQueryParams();

  const {
    filter,
    page,
    limit,
    keyword,
    handlePagination,
    handleLimit,
    handleSearch,
    handleFilter,
  } = useTable<GetEmployeeManagementDataParamsType>({
    defaultFilter: {
      search_type: "name",
      status: (query.status as GetEmployeeTypeParams["status"]) || "active",
    },
  });
  const {
    open,
    handleClose: handleCloseConfirm,
    handleOpen,
  } = useModal<string, ModalType>();

  const { isLoading, isError, error, data } = useGetEmployeeManagementDataQuery(
    {
      params: {
        ...filter,
        is_in_attendance_device: false,
        search_type: undefined,
        ...(keyword ? { search_type: filter.search_type } : {}),
        search: keyword,
      },
    }
  );
  const [
    createBulkUsers,
    {
      isError: isCreateBulkUserError,
      error: createBulkUserError,
      isLoading: isCreateBulkUserLoading,
      isSuccess: isCreateBulkUserSuccess,
    },
  ] = useCreateBulkUsersMutation();
  useError(isError, error, "Get employee management data fail");
  useError(
    isCreateBulkUserError,
    createBulkUserError,
    "Bluk Users create fail"
  );

  const columns: ColumnsType<
    GetEmployeeManagementDataResponseType["data"]["employees"][0]
  > = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "member",
      render: (val) => val?.profile?.name ?? "n/a",
    },
    {
      title: "Email",
      dataIndex: "member",
      render: (val) => val?.profile?.email ?? "n/a",
    },
    {
      title: "Department",
      dataIndex: "role",
      render: (val) => val?.department?.name ?? "n/a",
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status, record) => (
        <div className="flex items-center gap-2">
          <span className={Status[val]}>{capitalize(val)}</span>
        </div>
      ),
    },
    {
      title: "In Device",
      dataIndex: "is_in_attendance_device",
      render: (val) => (val === true ? "Yes" : val === false ? "No" : "n/a"),
    },
  ];

  const handleUpdateStatus = () => {
    const data = selectedData.map((item) => ({
      identifier: item.id,
      name: item.member?.profile?.name,
    }));

    createBulkUsers({ data });
  };

  useEffect(() => {
    if (isCreateBulkUserSuccess) {
      toast("success", "Bluk Users create successfully");
      handleCloseConfirm();
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateBulkUserSuccess]);

  return (
    <>
      <CustomModal
        title="Create Device User List"
        {...rest}
        handleClose={handleClose}
        width={1024}
        footer={null}
      >
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.employees)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowSelection={{
            type: "checkbox",
            // selectedRowKeys: selectedData,
            onChange: (_, selectedRows) => {
              setSelectedData(
                selectedRows as GetEmployeeManagementDataResponseType["data"]["employees"]
              );
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              <Select
                showSearch
                placeholder="Search Type"
                value={filter.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                popupMatchSelectWidth={false}
              />
              <Input
                key={filter.search_type}
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter.search_type || "",
                  true
                )}`}
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={(val) => handleSearch(val)}
                allowClear
              />

              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  Object.keys(Status),
                  "string",
                  true
                )}
                className="min-w-[90px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.status}
                onChange={(val) => handleFilter("status", val)}
              />

              {selectedData?.length ? (
                <Button
                  // icon={<AddIcon />}
                  type="primary"
                  className="sm-button !px-3"
                  onClick={() =>
                    handleOpen({
                      type: "BULK_ACTION",
                      // data: val,
                    })
                  }
                >
                  Create Device User
                </Button>
              ) : null}
            </>
          }
        />

        {open.type === "BULK_ACTION" && (
          <ConfirmModal
            open={open?.open}
            handleClose={handleCloseConfirm}
            handleOk={handleUpdateStatus}
            confirmTitle={`Create ${
              selectedData?.length === 1 ? "user" : "users"
            } in device`}
            questionText={`Are you sure to create ${
              selectedData?.length === 1 ? "user" : "users"
            } in device?`}
            okText="Confirm"
            okLoading={isCreateBulkUserLoading}
          />
        )}
      </CustomModal>
    </>
  );
}
